import { compose, concat, converge, head, join, map, split, tail, toLower, toUpper } from 'ramda';

import { INFO_NOT_PRESENT } from '../constants';
import { addHours, differenceInDays, differenceInHours, differenceInMinutes, formatDate, toDate, utcWallClock } from './date';

export const INTERNAL_DATE_FORMAT = 'yyyy-MM-dd';
export const INTERNAL_DATE_FORMAT_REGEXP = /^\d{4}-\d{2}-\d{2}$/;
export const DISPLAY_DATE_FORMAT = 'MM/dd/yyyy';
export const DISPLAY_DATE_PLACEHOLDER = 'MM/DD/YYYY';
export const SHORT_DATE_FORMAT = 'MMM d, yyyy';

export const DISPLAY_TIME_FORMAT = 'h:mm aaa';

export const DEPRECATED_DATE_TIME_FORMAT = 'MMM dd, yyyy | HH:mm';
export const DISPLAY_DATE_TIME_FORMAT = `${DISPLAY_DATE_FORMAT} ${DISPLAY_TIME_FORMAT}`;
export const DASHBOARD_DATE_TIME_FORMAT = `${DISPLAY_DATE_FORMAT} 'at' ${DISPLAY_TIME_FORMAT}`;
export const SCOUTING_REPORT_DATE_TIME_FORMAT = `${DISPLAY_DATE_FORMAT} 'at' ${DISPLAY_TIME_FORMAT}`;

const userTimezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
const userTimezoneAbbreviation =
  Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
    .formatToParts()
    .find(p => p.type === 'timeZoneName')?.value || '';

export const getTimezoneAbbreviation = (timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' }).formatToParts();

  return parts.find(part => part.type === 'timeZoneName')?.value || '';
};

export const userTimezone = {
  abbreviation: userTimezoneAbbreviation,
  name: userTimezoneName,
  utc_offset_hours: -1 * (new Date().getTimezoneOffset() / 60)
};

export const fixTZName = (name: string): string => `${name === 'Europe/Kiev' ? 'Europe/Kyiv' : name}`;

const ELLIPSIS_MAX_SYMBOLS_COUNT = 25;

const MONTHS_IN_YEAR = 12;

const MONEY_TYPE_FLAT = 'flat';

export const dateFormatter = (date: any, format = DISPLAY_DATE_FORMAT) => date && formatDate(date, format);

export const timeFormatter = (time: any, format = DISPLAY_TIME_FORMAT) => time && formatDate(time, format);

const DAYS_PER_MONTH = 30.436875;
const DAYS_PER_YEAR = 365.2425;

const relativeTimeFromNow = (date: Date): string => {
  const diffMs = Date.now() - date.getTime();
  const future = diffMs < 0;
  const abs = Math.abs(diffMs);

  const seconds = Math.round(abs / 1000);
  const minutes = Math.round(abs / 60000);
  const hours = Math.round(abs / 3600000);
  const days = Math.round(abs / 86400000);
  const months = Math.round(abs / (86400000 * DAYS_PER_MONTH));
  const years = Math.round(abs / (86400000 * DAYS_PER_YEAR));

  let phrase: string;
  if (seconds <= 44) {
    phrase = 'a few seconds';
  } else if (minutes <= 1) {
    phrase = 'a minute';
  } else if (minutes <= 44) {
    phrase = `${minutes} minutes`;
  } else if (hours <= 1) {
    phrase = 'an hour';
  } else if (hours <= 21) {
    phrase = `${hours} hours`;
  } else if (days <= 1) {
    phrase = 'a day';
  } else if (days <= 25) {
    phrase = `${days} days`;
  } else if (months <= 1) {
    phrase = 'a month';
  } else if (months <= 10) {
    phrase = `${months} months`;
  } else if (years <= 1) {
    phrase = 'a year';
  } else {
    phrase = `${years} years`;
  }

  return future ? `in ${phrase}` : `${phrase} ago`;
};

export const relativeTimeFormatter = (time: any) => {
  if (!time) {
    return time;
  }
  const parsed = toDate(time);

  return parsed ? relativeTimeFromNow(parsed) : 'Invalid date';
};

export const calculateTimeWithOffset = (offset: any) => {
  const utcNow = addHours(utcWallClock(new Date()), offset);

  return formatDate(utcNow, 'h:mma');
};

export const dateTimeFormatter = (dateTime: any, format = DISPLAY_DATE_TIME_FORMAT) =>
  dateTime && formatDate(dateTime, format);

export const moneyFormatter = (money: any, isInteger = false, notation: any = 'standard') =>
  money &&
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation,
    maximumFractionDigits: isInteger ? 0 : 2,
    minimumFractionDigits: isInteger ? 0 : 2
  }).format(money);

export const percentMoneyFormatter = (money: any) => `${money}%`;

export const typedMoneyFormatter = (type: string | null | undefined, money: any, isInteger = false) => {
  if (!type || !money) {
    return '';
  }

  return type === MONEY_TYPE_FLAT ? moneyFormatter(money, isInteger) : percentMoneyFormatter(money);
};

export const premiumFormatter = (premium: any) =>
  premium && `${moneyFormatter(premium)} (${moneyFormatter(premium / MONTHS_IN_YEAR, true)}/mo)`;

export const phoneFormatter = (phone: any) =>
  phone && phone.replace(/[^+0-9<>em/]/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');

export const phoneFormatterBracesless = (phone: any) =>
  phone && phone.replace(/[^+0-9<>em/]/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');

export const fileSizeFormatter = (size: any) =>
  ['bytes', 'KB', 'MB'].reduce(
    (acc, item, i) => (size >= Math.pow(2, 10 * i) ? `${(size / Math.pow(2, 10 * i)).toFixed(2)} ${item}` : acc),
    INFO_NOT_PRESENT
  );

export const daysToNow = (date: any) => {
  if (!date) {
    return date;
  }
  const parsed = toDate(date);

  return parsed ? differenceInDays(new Date(), parsed) : NaN;
};

export const minutesAndHoursToNow = (date: any): string => {
  if (!date) {
    return '';
  }
  const past = toDate(date);
  if (!past) {
    return '';
  }
  const now = new Date();

  const minutes = differenceInMinutes(now, past);
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }

  const hours = differenceInHours(now, past);
  if (hours < 24) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }

  const days = differenceInDays(now, past);

  return `${days} day${days !== 1 ? 's' : ''}`;
};

export const humanize = (str: any) => str && str.replace(/[_\s]+/g, ' ');

export const capitalize = (str: any) => str && str[0].toUpperCase() + humanize(str).slice(1);

export const ellipsis = (str: any, maxSymbols = ELLIPSIS_MAX_SYMBOLS_COUNT) => {
  if (!str) {
    return '';
  }
  if (str.length <= maxSymbols) {
    return str;
  }

  return `${str.slice(0, maxSymbols)}...`;
};

export const formatFileName = (name: string) => name.replace(/\s/g, '_');
// @ts-expect-error TODO
const capitalizeText = converge(concat(), [compose(toUpper, head), tail]);
export const titleize = compose(join(' '), map(capitalizeText), split(' '), toLower);

export const getOrdinal = (n: number) => {
  let ord = 'th';

  if (n % 10 === 1 && n % 100 !== 11) {
    ord = 'st';
  } else if (n % 10 === 2 && n % 100 !== 12) {
    ord = 'nd';
  } else if (n % 10 === 3 && n % 100 !== 13) {
    ord = 'rd';
  }

  return ord;
};
